import { FC, useEffect, useState } from 'react';

import dayjs from 'dayjs';
import { CalendarDays, Plus } from 'lucide-react';

import { QuestionsCircleIcon, Button, EmptyState, ActionTile, Tooltip, Heading } from '../../../shared/ui';
import { SettingsButton } from '../../icons/settings-button';
import { getTimeTzNoLess } from '../../../utils/getTime';
import { PlusIcon } from '../../icons/plus';
import { CalendarIcon } from '../../icons/calendar';
import { IClient, IConsultation } from '../../../store/types';
import { ConsultationCard } from '../consultation-card';
import { UploadAudioWidget } from '../upload-audio-widget';

type ConsultationTableProps = {
  setOpenSettings: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenCalendar: React.Dispatch<React.SetStateAction<boolean>>;
  openForm: () => void;
  consultationList: IConsultation[];
  /** While true the list is unknown, so the empty state stays hidden. */
  isLoading?: boolean;
  clientsList: Pick<IClient, 'id' | 'full_name' | 'time_zone'>[];
  handleChangeCons: (
    id: number,
    options?: { isEdit?: boolean; overrideStart?: string; overrideEnd?: string; overrideDate?: string },
  ) => void;
  handleOpenDelete: (item: IConsultation, isCalendar: boolean) => void;
  openEditFormClient: (id: number) => void;
  sortConsultation: () => void;
};

export const ConsultationTable: FC<ConsultationTableProps> = ({
  setOpenSettings,
  setOpenCalendar,
  handleChangeCons,
  openForm,
  openEditFormClient,
  consultationList,
  isLoading,
  clientsList,
  sortConsultation,
  handleOpenDelete,
}) => {
  const [time, setTime] = useState(getTimeTzNoLess(new Date()));

  useEffect(() => {
    //Implementing the setInterval method
    const interval = setInterval(() => {
      setTime(getTimeTzNoLess(new Date()));
    }, 1000);

    //Clearing the interval
    return () => clearInterval(interval);
  }, [time]);

  return (
    <div className="flex flex-col gap-10 sm:gap-3 w-full h-full">
      <div className="w-full flex flex-col gap-[30px] sm:flex-row justify-between relative">
        <div className="flex gap-4 items-center ">
          <Heading size="xl">Консультации</Heading>
          <Tooltip text="На странице отображаются встречи на СЕГОДНЯ, остальные – в Календаре встреч.">
            <QuestionsCircleIcon width={21} height={21} />
          </Tooltip>
        </div>
        <Button
          size="md"
          variant="dark"
          onClick={() => setOpenSettings(true)}
          className="max-sm:hidden"
          startIcon={<SettingsButton className="fill-white" />}
        >
          Настройки
        </Button>
      </div>
      <div className="px-5 pb-5 pt-6 flex flex-col sm:flex-row justify-between w-full h-full gap-5 bg-surface-muted rounded-t-popup">
        <div className="w-full sm:max-w-[180px] flex flex-col gap-[30px]">
          <div className="grid grid-cols-2 gap-5 sm:flex sm:flex-col sm:gap-[30px]">
            <div className="flex h-[126px] w-full flex-col rounded-card bg-white px-4 py-5 sm:max-w-[180px] sm:px-[30px]">
              <p className="text-ink-subtle text-ui mb-[5px]">Сегодня</p>
              <p className="mb-2.5 text-display-s tabular-nums text-primary">{time}</p>
              <p className="whitespace-nowrap text-body-l-strong text-ink">{dayjs().format('DD.MM.YYYY, dd')}</p>
            </div>
            <ActionTile
              icon={<CalendarIcon className="stroke-white" />}
              className="sm:max-w-[180px]"
              onClick={() => setOpenCalendar((prev) => !prev)}
            >
              Календарь встреч
            </ActionTile>
          </div>
          <ActionTile
            size="md"
            icon={<PlusIcon className="stroke-white" />}
            className="sm:max-w-[180px]"
            onClick={() => openForm()}
          >
            Создать встречу
          </ActionTile>
          <UploadAudioWidget clientNames={clientsList.map((c) => c.full_name).filter(Boolean)} />
        </div>
        <div className="w-full h-full flex flex-col gap-10 pb-5 min-w-0">
          {!isLoading && consultationList.length === 0 && (
            <EmptyState
              icon={<CalendarDays size={24} strokeWidth={1.5} />}
              title="На сегодня встреч нет"
              action={
                <Button size="md" startIcon={<Plus size={18} />} onClick={() => openForm()}>
                  Создать встречу
                </Button>
              }
            >
              Запланируйте сессию, и клиент получит ссылку на встречу.
            </EmptyState>
          )}
          {consultationList?.map((item, idx) => {
            const client = clientsList.find((el) => `/api/v1/clients/${el.id}` === item.client_entity);

            return (
              <ConsultationCard
                openEditFormClient={openEditFormClient}
                item={item}
                handleChangeCons={handleChangeCons}
                handleOpenDelete={handleOpenDelete}
                client={client}
                key={idx}
                sortConsultation={sortConsultation}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};
